import {
  Button, ButtonSize, ButtonVariant, Icon, IconName,
} from '@usealloy/component-library';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import { RED } from '~/constants';
import { snakeToCaps } from '~/helpers/formatting';


// Display formats
export const MOMENT_DISPLAY_FORMAT = 'M/D/YY h:mm A';
export const DATEPICKER_DISPLAY_FORMAT = 'M/d/yy h:mm a';


// Quick select options
export const QUICK_DATETIME_OPTIONS = [
  { text: 'Today', value: [moment().startOf('day').toDate(), 'now'] },
  { text: 'This week', value: [moment().startOf('week').toDate(), 'now'] },
  { text: 'This month', value: [moment().startOf('month').toDate(), 'now'] },
  { text: 'This quarter', value: [moment().startOf('quarter').toDate(), 'now'] },
  { text: 'This year', value: [moment().startOf('year').toDate(), 'now'] },
  { text: 'Last 15 minutes', value: [[15, 'minutes'], 'now'] },
  { text: 'Last 30 minutes', value: [[30, 'minutes'], 'now'] },
  { text: 'Last 1 hour', value: [[1, 'hours'], 'now'] },
  { text: 'Last 3 hours', value: [[3, 'hours'], 'now'] },
  { text: 'Last 6 hours', value: [[6, 'hours'], 'now'] },
  { text: 'Last 12 hours', value: [[12, 'hours'], 'now'] },
  { text: 'Last 24 hours', value: [[1, 'days'], 'now'] },
  { text: 'Last 3 days', value: [[3, 'days'], 'now'] },
  { text: 'Last 7 days', value: [[7, 'days'], 'now'] },
  { text: 'Last 30 days', value: [[30, 'days'], 'now'] },
  { text: 'Last 3 months', value: [[90, 'days'], 'now'] },
  { text: 'Last 6 months', value: [[180, 'days'], 'now'] },
  { text: 'Last 1 year', value: [[365, 'days'], 'now'] },
  { text: 'Last 2 years', value: [[730, 'days'], 'now'] },
  { text: 'Last 3 years', value: [[1095, 'days'], 'now'] },
];


// Convert a quick
export function convertQuickToAbsolute(quickValue) {
  if (quickValue && quickValue[1] === 'now') {
    const now = moment.utc();
    const start = moment.utc(now).subtract(...quickValue[0]);
    return [start, now];
  }
  return [];
}

const StyledDateInput = styled.input`
  &::-webkit-inner-spin-button,
  &::-webkit-calendar-picker-indicator {
      display: none;
      -webkit-appearance: none;
  }
`;


const DateTimeInputPropTypes = {
  value: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  absDate: PropTypes.object.isRequired,
};

const isValidDate = date => date instanceof Date && !isNaN(date);

export const DateTimeInput = React.memo(({
  value,
  onClick,
  onUpdate,
  absDate,
}) => {
  const [currentDate, setCurrentDate] = useState(
    (value && absDate) ? moment(value).format('YYYY-MM-DD') : '',
  );

  useEffect(() => {
    if (absDate) setCurrentDate(moment(absDate).format('YYYY-MM-DD'));
  }, [absDate]);

  const handleInputChange = event => {
    setCurrentDate(event.target.value);
    const dateObj = new Date(event.target.value);

    // conversion to date obj sets date exatly 1 day behind
    dateObj.setDate(dateObj.getDate() + 1);
    if (isValidDate(dateObj)) {
      onUpdate(dateObj);
    }
  };

  return (
    <a
      onClick={onClick}
    >
      <StyledDateInput onChange={handleInputChange} value={currentDate} type="date" className="date-input" />
      <Icon icon={IconName.Calendar} />
    </a>
  );
});
DateTimeInput.propTypes = DateTimeInputPropTypes;
DateTimeInput.displayName = 'DateTimeInput';

const DateTimeFilterPropTypes = {
  isOpen: PropTypes.bool,
  selectedRange: PropTypes.array,
  filter: PropTypes.shape({
    prop: PropTypes.string.isRequired,
    label: PropTypes.label,
  }).isRequired,
  updateFilterBy: PropTypes.func.isRequired,
  isClearDateInput: PropTypes.bool.isRequired,
  setIsClearDateInput: PropTypes.func.isRequired,
};

const DateTimeFilterDefaultProps = {
  isOpen: false,
  selectedRange: null,
};

const DateTimeFilter = React.memo(({
  isOpen,
  selectedRange,
  updateFilterBy,
  filter,
  isClearDateInput,
  setIsClearDateInput,
}) => {
  const startDatePicker = useRef(null);
  const endDatePicker = useRef(null);

  const TABS = ['quick', 'absolute', 'all_time'];

  // DateTimeFilter state
  const getTabFromRange = range => (
    range && isValidDate(range[0]) && isValidDate(range[1]) ? 'absolute' : 'quick'
  );

  const [open, setOpen] = useState(isOpen);
  const [selectedTab, setSelectedTab] = useState(getTabFromRange(selectedRange));
  const [absoluteStart, setAbsoluteStart] = useState(null);
  const [absoluteEnd, setAbsoluteEnd] = useState(null);
  const [absoluteError, setAbsoluteError] = useState('');


  useEffect(() => {
    if (isClearDateInput) {
      setAbsoluteStart(null);
      setAbsoluteEnd(null);
      setIsClearDateInput(false);
    }
  }, [isClearDateInput, setIsClearDateInput]);

  const handleToggleOpen = () => setOpen(!open);
  const handleUpdateSelectedTab = event => {
    const { tab } = event.target.dataset;
    if (tab === 'all_time') {
      updateFilterBy(filter.prop, null);
      setOpen(false);
      setSelectedTab('quick');
    } else {
      setSelectedTab(tab);
    }
  };


  const formatDateRange = useCallback(range => {
    if (Array.isArray(range[0]) && range[1] === 'now') {
      const option = QUICK_DATETIME_OPTIONS
        .find(o => o.value[0][0] === range[0][0] && o.value[0][1] === range[0][1]);
      return option ? option.text : '';
    }
    return range.map(d => moment(d).format(MOMENT_DISPLAY_FORMAT)).join(' to ');
  }, []);


  // Event handlers
  const handleUpdateFilterByQuick = event => {
    const optionValue = QUICK_DATETIME_OPTIONS
      .find(o => o.text === event.target.text).value.slice();
    if (!Array.isArray(optionValue[0])) optionValue[1] = new Date();
    updateFilterBy(filter.prop, optionValue);
    setOpen(false);
  };

  const renderQuickTab = () => (
    <div className="datetime quick-options flex flex-col flex-wrap align-start header-8">
      {QUICK_DATETIME_OPTIONS.map(option => (
        <a
          key={option.text}
          data-quick="true"
          className="secondary"
          onClick={handleUpdateFilterByQuick}
        >
          {option.text}
        </a>
      ))}
    </div>
  );


  const handleUpdateAbsoluteStart = date => setAbsoluteStart(date);
  const handleUpdateAbsoluteEnd = date => {
    const currentDate = new Date();
    if (date <= currentDate) {
      setAbsoluteEnd(date);
    } else {
      setAbsoluteEnd(currentDate);
    }
  };
  const handleUpdateAbsoluteStartToNow = () => setAbsoluteStart(new Date());
  const handleUpdateAbsoluteEndToNow = () => setAbsoluteEnd(new Date());
  const handleSubmitAbsoluteValues = () => {
    const startValid = absoluteStart && isValidDate(absoluteStart);
    const endValid = absoluteEnd && isValidDate(absoluteEnd);
    if (!startValid || !endValid) {
      setAbsoluteError('One or both selected dates are not valid.');
    } else {
      updateFilterBy(filter.prop, [absoluteStart, absoluteEnd]);
      setAbsoluteError('');
    }
  };

  const renderAbsoluteTab = () => {
    const datePickerProps = {
      showTimeInput: true,
      timeInputLabel: 'Time: ',
      dateFormat: DATEPICKER_DISPLAY_FORMAT,
      showMonthDropdown: true,
      showYearDropdown: true,
      scrollableYearDropdown: true,
      shouldCloseOnSelect: false,
    };

    return (
      <div className="datetime">
        {absoluteError ? (
          <div className="flex start header-8 h-space-8 pad-8-16 small-text red">
            <Icon icon={IconName.Exclamation} color={RED.red600} />
            <span>{absoluteError}</span>
          </div>
        ) : false}

        <div className="flex start no-wrap h-space-8 header-16">
          <DatePicker
            ref={startDatePicker}
            selected={absoluteStart}
            selectsStart
            selectsEnd={false}
            maxDate={absoluteEnd || new Date()}
            startDate={absoluteStart}
            endDate={absoluteEnd}
            onChange={handleUpdateAbsoluteStart}
            customInput={
              (
                <DateTimeInput
                  onUpdate={date => handleUpdateAbsoluteStart(date)}
                  absDate={absoluteStart}
                />
              )
            }
            {...datePickerProps}
          >
            <a className="now-button" onClick={handleUpdateAbsoluteStartToNow}>Now</a>
          </DatePicker>

          <span key={-1} className="to-span">to</span>

          <DatePicker
            ref={endDatePicker}
            selected={absoluteEnd}
            selectsStart={false}
            selectsEnd
            minDate={absoluteStart}
            maxDate={new Date()}
            startDate={absoluteStart}
            endDate={absoluteEnd}
            onChange={handleUpdateAbsoluteEnd}
            customInput={
              (
                <DateTimeInput
                  onUpdate={date => handleUpdateAbsoluteEnd(date)}
                  absDate={absoluteEnd}
                />
              )
            }
            {...datePickerProps}
          >
            <a className="now-button" onClick={handleUpdateAbsoluteEndToNow}>Now</a>
          </DatePicker>
        </div>

        <div className="flex end h-space-8 header-8">
          <Button
            onClick={handleSubmitAbsoluteValues}
            size={ButtonSize.small}
            variant={ButtonVariant.primary}
          >
            Apply
          </Button>
        </div>
      </div>
    );
  };


  return (
    <Dropdown className="datetime-filter facet" isOpen={open} toggle={handleToggleOpen}>
      <DropdownToggle
        caret={false}
        className="secondary nocase"
      >
        <span className="h-space-4">
          {filter.label && <span>{`${filter.label}: `}</span>}
          {selectedRange && selectedRange.length > 1
            ? formatDateRange(selectedRange) : 'All time'}
        </span>
      </DropdownToggle>

      <DropdownMenu>
        <div className="flex start h-space-16 header-8 no-wrap datetime-tabs">
          {TABS.map(tab => {
            const selected = tab === selectedTab ? 'bold' : '';
            const black = ['quick', 'absolute'].indexOf(tab) > -1 ? 'minimal black' : '';
            return (
              <a
                key={tab}
                className={`secondary ${selected} ${black}`}
                data-tab={tab}
                onClick={handleUpdateSelectedTab}
              >
                {snakeToCaps(tab)}
              </a>
            );
          })}
        </div>

        {(() => {
          if (selectedTab === 'quick') return renderQuickTab();
          if (selectedTab === 'absolute') return renderAbsoluteTab();
          return false;
        })()}
      </DropdownMenu>
    </Dropdown>
  );
});
DateTimeFilter.propTypes = DateTimeFilterPropTypes;
DateTimeFilter.defaultProps = DateTimeFilterDefaultProps;
DateTimeFilter.displayName = 'DateTimeFilter';

export default DateTimeFilter;
